import { array, object } from "yup";
import { ConfiguredJob } from "common/state/rtcJobsApi";
import { FeatureCollection, LineString, Point, Polygon } from "geojson";
import {
  LatLngSchema,
  OBJECTIVE_BASE_PROPS,
  ObjectiveFeatureProperties,
} from "common/utils/rtcJobs";
import { Layer, Source } from "react-map-gl";
import { makeObjectiveRenderer, RestrictedObjective } from "../ObjectiveLayers";
import { Objective_ObjectiveType as ObjectiveType } from "protos/rtc/jobs";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import buffer from "@turf/buffer";
import React, { useMemo } from "react";

// Row width fallback when it can't be calculated from the planting path
export const DEFAULT_ROW_WIDTH = 6.7056;

const InSeasonRowSchema = object({
  path: object({
    points: array().of(LatLngSchema.required()).min(2).required(),
  }).required(),
});

type InSeasonRowObjective = RestrictedObjective<
  ObjectiveType.IN_SEASON_ROW,
  typeof InSeasonRowSchema
>;

function rowLinesForInSeasonRowObjectives(
  objectives: InSeasonRowObjective[]
): FeatureCollection<LineString, ObjectiveFeatureProperties> {
  return {
    type: "FeatureCollection",
    features: objectives.flatMap((o) => {
      const properties: ObjectiveFeatureProperties = {
        ...OBJECTIVE_BASE_PROPS,
        objective: {
          ...o,
        },
      };
      return {
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: o.data.path.points.map((p) => [p.lng, p.lat]),
        },
        properties,
      };
    }),
  };
}

function rowBoxesForInSeasonRowObjectives(
  configuredJob: ConfiguredJob,
  objectives: InSeasonRowObjective[]
): FeatureCollection<Polygon, ObjectiveFeatureProperties> {
  const rowWidth = configuredJob.inSeason?.rowSpacingM || DEFAULT_ROW_WIDTH;

  return {
    type: "FeatureCollection",
    features: objectives.flatMap((o) => {
      const points = o.data.path.points;
      if (points.length < 2) {
        return [];
      }
      const lineGeoJson: LineString = {
        type: "LineString",
        coordinates: points.map((p) => [p.lng, p.lat]),
      };

      const bufferedLine = buffer(lineGeoJson, rowWidth / 2, {
        units: "meters",
        steps: 1,
      });
      const properties: ObjectiveFeatureProperties = {
        ...OBJECTIVE_BASE_PROPS,
        objective: {
          ...o,
        },
      };

      return [
        {
          ...bufferedLine,
          properties,
        },
      ];
    }),
  };
}

function endPointsForInSeasonRowObjectives(
  objectives: InSeasonRowObjective[]
): FeatureCollection<Point, ObjectiveFeatureProperties> {
  return {
    type: "FeatureCollection",
    features: objectives.flatMap((o) => {
      const properties: ObjectiveFeatureProperties = {
        ...OBJECTIVE_BASE_PROPS,
        objective: {
          ...o,
        },
      };
      const first = o.data.path.points[0];
      const last = o.data.path.points.at(-1);
      if (!first || !last) {
        return [];
      }
      return [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [first.lng, first.lat],
          },
          properties,
        },
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [last.lng, last.lat],
          },
          properties,
        },
      ];
    }),
  };
}

enum LayerIds {
  ROW_NUMBERS = "isr-row-numbers",
  CENTERLINES = "isr-centerlines",
  CENTERLINES_HIGHLIGHT = "isr-centerlines-highlighted",
  CENTERLINES_HIGHLIGHT_FIRST = "isr-centerlines-highlighted-first",
  BOX_OUTLINE = "isr-row-outline",
  BOX_FILL = "isr-row-fill",
  BOX_FILL_PATTERN = "isr-row-fill-pattern",
  BOX_FOCUSED = "isr-focused",
  BOX_SELECTED_OUTLINE = "isr-selected-outline",
  BOX_SELECTED = "isr-selected-fill",
  BOX_TRACTOR_HOVER = "isr-tractor-hover",
  BOX_HIGHLIGHTED = "isr-highlighted",
  BOX_HIGHLIGHTED_FIRST = "isr-first-highlighted",
}

export const renderInSeasonRowObjective = makeObjectiveRenderer({
  type: ObjectiveType.IN_SEASON_ROW,
  dataSchema: InSeasonRowSchema,
  Render: ({ configuredJob, objectives, beforeId, api }) => {
    const endPoints = useMemo(
      () => endPointsForInSeasonRowObjectives(objectives),
      [objectives]
    );
    const rowLines = useMemo(
      () => rowLinesForInSeasonRowObjectives(objectives),
      [objectives]
    );
    const rowBoxes = useMemo(
      () => rowBoxesForInSeasonRowObjectives(configuredJob, objectives),
      [configuredJob, objectives]
    );

    return (
      <>
        <Source data={endPoints} type="geojson">
          <Layer
            id={LayerIds.ROW_NUMBERS}
            beforeId={beforeId}
            type="symbol"
            paint={{
              "text-color": ["coalesce", api.getTractorAssignmentColor, "#FFF"],
              "text-halo-color": theme.colors.black,
              "text-halo-width": 2,
            }}
            layout={{
              "text-field": ["get", "ordinal", api.getObjective],
            }}
          />
        </Source>
        <Source data={rowLines} type="geojson">
          <Layer
            id={LayerIds.CENTERLINES}
            beforeId={LayerIds.ROW_NUMBERS}
            type="line"
            minzoom={14}
            maxzoom={24}
            paint={{
              "line-color": theme.colors.black,
              "line-dasharray": [16, 8],
            }}
          />
          <Layer
            id={LayerIds.CENTERLINES_HIGHLIGHT}
            beforeId={LayerIds.ROW_NUMBERS}
            type="line"
            paint={{
              "line-color": theme.colors.black,
              "line-width": 2,
            }}
            filter={api.isHighlightedObjective}
          />
          <Layer
            id={LayerIds.CENTERLINES_HIGHLIGHT_FIRST}
            beforeId={LayerIds.ROW_NUMBERS}
            type="line"
            paint={{
              "line-color": theme.colors.white,
              "line-width": 2,
            }}
            filter={api.isFirstHighlightedObjective}
          />
        </Source>
        <Source data={rowBoxes} type="geojson">
          <Layer
            id={LayerIds.BOX_OUTLINE}
            beforeId={LayerIds.CENTERLINES}
            minzoom={16}
            maxzoom={24}
            type="line"
            paint={{
              "line-color": theme.colors.black,
            }}
          />
          <Layer
            id={LayerIds.BOX_FILL}
            beforeId={LayerIds.CENTERLINES}
            type="fill"
            paint={{
              "fill-color": ["coalesce", api.getTractorAssignmentColor, "#000"],
              "fill-opacity": api.getTractorAssignmentOpacity,
            }}
          />
          <Layer
            id={LayerIds.BOX_FILL_PATTERN}
            beforeId={LayerIds.CENTERLINES}
            type="fill"
            paint={{
              "fill-pattern": api.getTexture,
              "fill-color": ["coalesce", api.getTractorAssignmentColor, "#000"],
              "fill-opacity": api.getTractorAssignmentOpacity,
            }}
          />
          <Layer
            id={LayerIds.BOX_FOCUSED}
            beforeId={LayerIds.CENTERLINES}
            type="fill"
            paint={{
              "fill-color": theme.colors.blue[400],
              "fill-opacity": 0.6,
            }}
            filter={api.isFocusedObjective}
          />
          <Layer
            id={LayerIds.BOX_SELECTED_OUTLINE}
            beforeId={LayerIds.CENTERLINES}
            type="line"
            paint={{
              "line-color": theme.colors.blue[600],
              "line-width": 2,
            }}
            filter={api.isSelectedObjective}
          />
          <Layer
            id={LayerIds.BOX_SELECTED}
            beforeId={LayerIds.CENTERLINES}
            type="fill"
            paint={{
              "fill-color": theme.colors.blue[500],
              "fill-opacity": 0.2,
            }}
            filter={api.isSelectedObjective}
          />
          <Layer
            id={LayerIds.BOX_TRACTOR_HOVER}
            beforeId={beforeId}
            type="line"
            paint={{
              "line-color": theme.colors.black,
              "line-width": 2,
            }}
            filter={api.isFocusedTractorObjective}
          />
          <Layer
            id={LayerIds.BOX_HIGHLIGHTED}
            beforeId={beforeId}
            type="line"
            paint={{
              "line-color": theme.colors.black,
              "line-width": 2,
            }}
            filter={api.isHighlightedObjective}
          />
          <Layer
            id={LayerIds.BOX_HIGHLIGHTED_FIRST}
            beforeId={LayerIds.BOX_FILL}
            type="line"
            paint={{
              "line-blur": 8,
              "line-color": theme.colors.white,
              "line-width": 16,
            }}
            filter={api.isFirstHighlightedObjective}
          />
        </Source>
      </>
    );
  },
  interactiveLayerIds: [LayerIds.BOX_FILL],
});
